"use client"

import { useState, useEffect, Suspense } from "react"
import Link from "next/link"
import { useUser } from "@stackframe/stack"
import { Button } from "@/components/ui/button"
import { Menu, X, BookOpen, User as UserIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { usePathname } from "next/navigation"
import { usePermissions } from "@/hooks/use-permissions"
import { getUserPermissions } from "@/lib/services/rbac-service";


const navLinks = [
  { href: "/", label: "首页" },
  { href: "/subjects", label: "科目" },
  { href: "/questions", label: "题库" },
  { href: "/study-plan", label: "学习计划" },
  { href: "/about", label: "关于" },
]

// 用户区域骨架屏组件
const UserSkeleton = () => (
  <div className="hidden md:flex items-center gap-4">
    <div className="h-9 w-16 bg-muted rounded animate-pulse"></div>
    <div className="h-9 w-16 bg-muted rounded animate-pulse"></div>
  </div>
)

// 移动端用户区域骨架屏
const MobileUserSkeleton = () => (
  <div className="mt-8 flex flex-col gap-4">
    <div className="h-10 w-full bg-muted rounded animate-pulse"></div>
    <div className="h-10 w-full bg-muted rounded animate-pulse"></div>
  </div>
)


// 用户相关的组件，处理异步用户状态
const UserSection = ({
  isMobile = false,
  onMenuClose,
}: {
  isMobile?: boolean
  onMenuClose?: () => void
}) => {
  const user = useUser()

  const { setPermissionsAndRoles, isReady } = usePermissions();

  useEffect(() => {
    if (isReady) return;

    const fetchPermissions = async () => {
      if (user) {
        try {
          const data = await getUserPermissions(user.id);
          setPermissionsAndRoles(data.permissions.map(p => p.name), data.roles.map(r => r.name));
        } catch (error) {
          console.error("Failed to fetch user permissions:", error);
          setPermissionsAndRoles([], []);
        }
      } else {
        setPermissionsAndRoles([], []);
      }
    };

    fetchPermissions();
  }, [user, setPermissionsAndRoles, isReady]);

  if (isMobile) {
    return (
      <div className="mt-8 flex flex-col gap-4">
        {user ? (
          <>
            <Button variant="outline" asChild>
              <Link
                href="/settings"
                className="flex items-center justify-center gap-2"
                onClick={onMenuClose}
              >
                <UserIcon className="h-4 w-4" />
                个人设置
              </Link>
            </Button>
            <Button asChild>
              <Link href="/handler/sign-out" onClick={onMenuClose}>
                退出
              </Link>
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" asChild>
              <Link href="/handler/login" onClick={onMenuClose}>
                登录
              </Link>
            </Button>
            <Button asChild>
              <Link href="/handler/signup" onClick={onMenuClose}>
                注册
              </Link>
            </Button>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="hidden md:flex items-center gap-4">
      {user ? (
        <>
          <Button variant="ghost" asChild>
            <Link href="/settings" className="flex items-center gap-2">
              <UserIcon className="h-4 w-4" />
              个人设置
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link href="/handler/sign-out">退出</Link>
          </Button>
        </>
      ) : (
        <>
          <Button variant="ghost" asChild>
            <Link href="/handler/login">登录</Link>
          </Button>
          <Button asChild>
            <Link href="/handler/signup">注册</Link>
          </Button>
        </>
      )}
    </div>
  )
}

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  // 页面是管理侧的时候，header下有一条border 用来分割
  const isAdminPage = usePathname().startsWith("/admin")

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        isScrolled || isMenuOpen
          ? "bg-background/95 backdrop-blur-sm shadow-md"
          : "bg-transparent",
        isAdminPage ? "border-b" : ""
      )}
    >
      <div className="container mx-auto flex h-16 items-center justify-between px-4 md:px-6">
        <Link href="/" className="flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-primary" />
          <span className="font-bold text-lg">软考通</span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <nav className="flex items-center gap-6">
            {navLinks.map(link => (
              <Link
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        <Suspense fallback={<UserSkeleton />}>
          <UserSection />
        </Suspense>

        <div className="md:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </Button>
        </div>
      </div>

      {isMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-background shadow-lg p-4">
          <div className="container mx-auto">
            <nav className="flex flex-col gap-4">
              {navLinks.map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-base font-medium text-muted-foreground hover:text-primary"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            <Suspense fallback={<MobileUserSkeleton />}>
              <UserSection
                isMobile={true}
                onMenuClose={() => setIsMenuOpen(false)}
              />
            </Suspense>
          </div>
        </div>
      )}
    </header>
  )
}
